import * as R from 'remeda';
import type {
  CanvasContext,
  CanvasTransform,
  CoordinateTransformContext,
} from '../coordinate-system';
import {
  coordinates,
  getCoordinateTransformContext,
} from '../coordinate-system';
import type { Dimensions, Point2D, Vector2D } from '../utils';

export const getCanvasContext = (state: {
  transform: CanvasTransform;
  viewportRect: CoordinateTransformContext['viewportRect'];
  dimensions: Dimensions;
  padding: CoordinateTransformContext['contentPadding'];
}): CanvasContext => ({
  ...getCoordinateTransformContext(state),
  contentDimensions: state.dimensions,
});

const asCtx = (ctx: CanvasContext, t: CanvasTransform): CanvasContext => ({
  ...ctx,
  canvasTransform: t,
});

const contentFitsX = (ctx: CanvasContext, zoom: number): boolean => {
  const visibleWidth =
    ctx.viewportRect.width -
    (ctx.contentPadding.left + ctx.contentPadding.right);
  return ctx.contentDimensions.width * zoom <= visibleWidth;
};

const contentFitsY = (ctx: CanvasContext, zoom: number): boolean => {
  const visibleHeight =
    ctx.viewportRect.height -
    (ctx.contentPadding.top + ctx.contentPadding.bottom);
  return ctx.contentDimensions.height * zoom <= visibleHeight;
};

const contentFitsInViewport = (ctx: CanvasContext, zoom: number): boolean =>
  contentFitsX(ctx, zoom) && contentFitsY(ctx, zoom);

export const chooseZoomOrigin = (
  ctx: CanvasContext,
  currentZoom: number,
  preferredOrigin?: Point2D
): Point2D => {
  const viewportCenter = {
    x: ctx.viewportRect.width / 2,
    y: ctx.viewportRect.height / 2,
  };

  return contentFitsInViewport(ctx, currentZoom)
    ? viewportCenter
    : (preferredOrigin ?? viewportCenter);
};

export const centerX =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => {
    const scaledWidth = ctx.contentDimensions.width * t.zoom;
    const visibleWidth =
      ctx.viewportRect.width -
      (ctx.contentPadding.left + ctx.contentPadding.right);
    return {
      ...t,
      translateX: (visibleWidth - scaledWidth) / 2,
    };
  };

export const centerY =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => {
    const scaledHeight = ctx.contentDimensions.height * t.zoom;
    const visibleHeight =
      ctx.viewportRect.height -
      (ctx.contentPadding.top + ctx.contentPadding.bottom);
    return {
      ...t,
      translateY: (visibleHeight - scaledHeight) / 2,
    };
  };

export const center =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform =>
    R.pipe(t, centerX(ctx), centerY(ctx));

export const fitX =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => {
    const totalHorizontalPadding =
      ctx.contentPadding.left + ctx.contentPadding.right;
    return {
      ...t,
      zoom:
        (ctx.viewportRect.width - totalHorizontalPadding) /
        ctx.contentDimensions.width,
    };
  };

export const translate =
  (delta: Vector2D) =>
  (transform: CanvasTransform): CanvasTransform => ({
    ...transform,
    translateX: transform.translateX - delta.x,
    translateY: transform.translateY - delta.y,
  });

export const translateToPosition =
  (position: Partial<Point2D>) =>
  (transform: CanvasTransform): CanvasTransform => ({
    ...transform,
    ...(position.x !== undefined && { translateX: position.x }),
    ...(position.y !== undefined && { translateY: position.y }),
  });

export const zoomAt =
  (
    ctx: CanvasContext,
    delta: number,
    origin: Point2D,
    constraints = { min: 0.2, max: 5 }
  ) =>
  (t: CanvasTransform): CanvasTransform => {
    const newZoom = R.clamp(t.zoom * (1 + delta), constraints);

    const anchorInContent = coordinates.point.viewportToContent(asCtx(ctx, t))(
      origin
    );

    const newAnchorPosition = coordinates.point.contentToViewport(
      asCtx(ctx, { ...t, zoom: newZoom })
    )(anchorInContent);

    return {
      zoom: newZoom,
      translateX: t.translateX + (origin.x - newAnchorPosition.x),
      translateY: t.translateY + (origin.y - newAnchorPosition.y),
    };
  };

const clampByAvailableSpace = (value: number, availableSpace: number): number =>
  availableSpace > 0
    ? R.clamp(value, { min: -availableSpace, max: 0 })
    : R.clamp(value, { min: 0, max: -availableSpace });

const availableSpace = (
  ctx: CanvasContext,
  t: CanvasTransform,
  axis: 'width' | 'height'
): number => {
  const padding =
    axis === 'width'
      ? ctx.contentPadding.left + ctx.contentPadding.right
      : ctx.contentPadding.top + ctx.contentPadding.bottom;
  const viewportSize =
    axis === 'width' ? ctx.viewportRect.width : ctx.viewportRect.height;
  const contentSize =
    (axis === 'width'
      ? ctx.contentDimensions.width
      : ctx.contentDimensions.height) * t.zoom;
  return contentSize + padding - viewportSize;
};

const clampX =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => ({
    ...t,
    translateX: clampByAvailableSpace(
      t.translateX,
      availableSpace(ctx, t, 'width')
    ),
  });

const clampY =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => ({
    ...t,
    translateY: clampByAvailableSpace(
      t.translateY,
      availableSpace(ctx, t, 'height')
    ),
  });

const clampToBounds =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform =>
    R.pipe(t, clampX(ctx), clampY(ctx));

export const centerIfFits =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform => {
    const centered = center(ctx)(t);

    return {
      ...t,
      translateX: contentFitsX(ctx, t.zoom)
        ? centered.translateX
        : t.translateX,
      translateY: contentFitsY(ctx, t.zoom)
        ? centered.translateY
        : t.translateY,
    };
  };

export const constrainToViewport =
  (ctx: CanvasContext) =>
  (t: CanvasTransform): CanvasTransform =>
    R.pipe(t, centerIfFits(ctx), clampToBounds(ctx));
